import React from 'react';
import { useTranslation } from 'react-i18next';
import { Qualification } from '../../../types';
import { Button } from '../../Common/button';

// Helper function to format date
const formatDate = (isoString: string | undefined | null, t: (key: string) => string, locale: string): string => { // Accepter undefined/null
  if (!isoString) return t('global.notAvailable');
  try {
    return new Date(isoString).toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return t('qualification.history.invalidDate');
  }
};

const REVIEW_TYPE_KEYS: Record<string, string> = {
  Legal: 'qualification.form.reviewTypeLegal',
  Tech: 'qualification.form.reviewTypeTech',
  Other: 'qualification.form.reviewTypeOther',
};

const MOSCOW_VALUES = new Set(['MUST', 'SHOULD', 'COULD', 'WONT']);

interface QualificationHistoryProps {
    history: Qualification[];
    onDelete: (qualificationId: string) => Promise<boolean>;
    onEdit: (qualification: Qualification) => void;
    isLoading?: boolean;
    /** ID of the currently authenticated user */
    currentUserId?: string;
    /** Whether the current user is an org admin or super-admin */
    isAdmin?: boolean;
}

const QualificationHistory: React.FC<QualificationHistoryProps> = ({
    history,
    onDelete,
    onEdit,
    isLoading = false,
    currentUserId,
    isAdmin = false,
}) => {
  const { t, i18n } = useTranslation();

  // Utiliser directement la prop 'history'
  if (!history || history.length === 0) {
    return (
      <div className="p-4 rounded-xl border border-border bg-card mt-4">
        <h3 className="text-lg font-semibold mb-2 text-foreground">{t('qualification.history.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('qualification.history.noHistory')}</p>
      </div>
    );
  }

  // Trier l'historique du plus récent au plus ancien en utilisant 'timestamp'
  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const handleDeleteClick = async (id: string) => {
      // On pourrait ajouter un état de chargement spécifique à la ligne ici
      await onDelete(id);
  }

  return (
    <div className="p-4 rounded-xl border border-border bg-card mt-4">
      <h3 className="text-lg font-semibold mb-4 text-foreground">{t('qualification.history.title')}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-muted/50">
            <tr>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('global.date')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('qualification.history.value')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('qualification.history.complexity')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('qualification.history.moscow')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('qualification.history.rice')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('global.notes')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('qualification.history.author')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('qualification.history.editor')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('qualification.history.review')}</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{t('global.actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedHistory.map((entry) => (
              <tr key={entry.id}>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-foreground/80">{formatDate(entry.timestamp, t, i18n.language)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-foreground">{entry.value_score ?? '-'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-foreground">{entry.complexity_score ?? '-'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-foreground">
                  {entry.moscow
                    ? (MOSCOW_VALUES.has(entry.moscow) ? t(`qualification.form.moscow.${entry.moscow}`) : entry.moscow)
                    : '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-foreground">
                    {/* Afficher les scores RICE de manière concise */}
                    R:{entry.rice_reach ?? '-'} I:{entry.rice_impact ?? '-'} C:{entry.rice_confidence ?? '-'} E:{entry.rice_effort ?? '-'}
                </td>
                 <td className="px-3 py-2 text-sm text-muted-foreground max-w-xs truncate" title={entry.notes ?? ''}>{entry.notes ?? '-'}</td>
                 {/* Afficher l'auteur */}
                 <td className="px-3 py-2 text-sm text-muted-foreground">
                     {entry.author ? (
                       <div className="space-y-0.5">
                         <div className="font-medium text-foreground">{entry.author.name}</div>
                         <div className="text-xs">{entry.author.email}</div>
                       </div>
                     ) : (
                       <span className="text-xs" title={entry.author_id}>{entry.author_id.substring(0, 8)}...</span>
                     )}
                 </td>
                 {/* Afficher l'éditeur */}
                 <td className="px-3 py-2 text-sm text-muted-foreground">
                     {entry.editor ? (
                       <div className="space-y-0.5">
                         <div className="font-medium text-foreground">{entry.editor.name}</div>
                         <div className="text-xs">{entry.editor.email}</div>
                       </div>
                     ) : entry.editor_id ? (
                       <span className="text-xs" title={entry.editor_id}>{entry.editor_id.substring(0, 8)}...</span>
                     ) : (
                       <span>-</span>
                     )}
                 </td>
                 {/* Afficher les informations de révision */}
                 <td className="px-3 py-2 text-sm text-foreground">
                     {entry.needs_review ? (
                       <div className="space-y-1">
                         <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                           {t('qualification.history.reviewRequired')}
                         </span>
                         {entry.review_type && (
                           <div className="text-xs text-muted-foreground">
                             {entry.review_type === 'Other' && entry.review_other_name
                               ? entry.review_other_name
                               : REVIEW_TYPE_KEYS[entry.review_type]
                                 ? t(REVIEW_TYPE_KEYS[entry.review_type])
                                 : entry.review_type}
                           </div>
                         )}
                       </div>
                     ) : (
                       <span className="text-muted-foreground">-</span>
                     )}
                 </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm space-x-2">
                    {(isAdmin || entry.author_id === currentUserId) && (
                      <>
                        <Button
                            onClick={() => onEdit(entry)}
                            disabled={isLoading}
                            variant="ghost"
                            className="text-primary"
                        >
                            {t('global.edit')}
                        </Button>
                        <Button
                            onClick={() => handleDeleteClick(entry.id)}
                            disabled={isLoading}
                            variant="ghost"
                            className="text-destructive"
                        >
                            {t('global.delete')}
                        </Button>
                      </>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QualificationHistory;
